import {useCallback, useEffect, useReducer} from "react";
import type {PlayerDisplay} from "@/player/PlayerViewHooks.ts";

export const PLAYER_VIEW_TRANSITION_DURATION_MS = 1000;

export function playerDisplayKey(display: PlayerDisplay): string {
    if (display.display === "encounter") return `encounter:${display.encounter.id}`;
    if (display.display === "scene") return `scene:${display.scene.id}`;
    return "campaign";
}

type Target = { display: PlayerDisplay; phase: "loading" | "fading" | "failed" };
type State = { current: PlayerDisplay; requestedKey: string; version: number; target?: Target };
type Action = { type: "request"; display: PlayerDisplay }
    | { type: "ready"; version: number; instant: boolean }
    | { type: "complete" | "fail"; version: number };

function transitionReducer(state: State, action: Action): State {
    if (action.type === "request") {
        const key = playerDisplayKey(action.display);
        return {
            ...state, requestedKey: key, version: state.version + 1,
            target: key === playerDisplayKey(state.current) ? undefined : {display: action.display, phase: "loading"}
        };
    }
    if (action.version !== state.version || !state.target) return state;
    if (action.type === "fail") return {...state, target: {...state.target, phase: "failed"}};
    if (action.type === "ready" && state.target.phase === "loading") {
        return action.instant ? {...state, current: state.target.display, target: undefined}
            : {...state, target: {...state.target, phase: "fading"}};
    }
    if (action.type === "complete" && state.target.phase === "fading") {
        return {...state, current: state.target.display, target: undefined};
    }
    return state;
}

export function usePlayerViewTransition(requested: PlayerDisplay) {
    const requestedKey = playerDisplayKey(requested);
    const [state, dispatch] = useReducer(transitionReducer, {
        current: requested, requestedKey, version: 0,
    });
    // Reconcile a new identity before committing children; object recreation is not a new request.
    if (state.requestedKey !== requestedKey) dispatch({type: "request", display: requested});
    const {version} = state;
    const ready = useCallback((key: string) => {
        if (key === requestedKey) dispatch({
            type: "ready", version,
            instant: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        });
    }, [requestedKey, version]);
    const fail = useCallback((key: string) => {
        if (key === requestedKey) dispatch({type: "fail", version});
    }, [requestedKey, version]);
    const phase = state.target?.phase;
    useEffect(() => {
        if (phase !== "fading") return;
        const timer = window.setTimeout(() => dispatch({
            type: "complete",
            version
        }), PLAYER_VIEW_TRANSITION_DURATION_MS);
        return () => window.clearTimeout(timer);
    }, [phase, version]);
    return {
        current: playerDisplayKey(state.current) === requestedKey ? requested : state.current,
        pending: state.target && phase !== "failed" ? requested : undefined,
        fading: phase === "fading", failed: phase === "failed", ready, fail,
    };
}
